export const SUPPORTED_CURRENCIES = ['EUR', 'PLN', 'GBP', 'SEK'];

const FALLBACK_RATES = {
  EUR: 1,
  PLN: 4.30,
  GBP: 0.85,
  SEK: 11.50,
};

const CURRENCY_SYMBOLS = {
  EUR: '€',
  PLN: 'zł',
  GBP: '£',
  SEK: 'kr',
};

// Map: currency code → country code used in countryUtils
export const CURRENCY_TO_COUNTRY = {
  EUR: null, // multi-pays, pas de mapping unique
  PLN: 'PL',
  GBP: 'UK',
  SEK: 'SE',
};

export const COUNTRY_TO_CURRENCY = {
  FR: 'EUR',
  BE: 'EUR',
  DE: 'EUR',
  ES: 'EUR',
  IT: 'EUR',
  NL: 'EUR',
  PL: 'PLN',
  UK: 'GBP',
  SE: 'SEK',
  IE: 'EUR',
};

/**
 * Fetch live exchange rates from Frankfurter (ECB proxy).
 * Returns an object { EUR:1, PLN:x, GBP:x, SEK:x, CZK:x }.
 * Falls back to FALLBACK_RATES on any error.
 */
export const fetchExchangeRates = async () => {
  try {
    const response = await fetch(
      'https://api.frankfurter.app/latest?from=EUR&to=PLN,GBP,SEK'
    );
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data = await response.json();
    return {
      EUR: 1,
      PLN: data.rates.PLN || FALLBACK_RATES.PLN,
      GBP: data.rates.GBP || FALLBACK_RATES.GBP,
      SEK: data.rates.SEK || FALLBACK_RATES.SEK,
    };
  } catch {
    console.warn('Frankfurter API unavailable, using fallback exchange rates');
    return { ...FALLBACK_RATES };
  }
};

/** Returns the symbol string for a currency code, defaults to '€'. */
export const getCurrencySymbol = (currency) =>
  CURRENCY_SYMBOLS[currency] || '€';

/**
 * Converts a local-currency amount to EUR.
 * rates: object returned by fetchExchangeRates (EUR-based, e.g. PLN:4.28 means 1€=4.28zł)
 */
export const convertToEUR = (amount, currency, rates) => {
  if (!currency || currency === 'EUR') return amount;
  const rate = (rates && rates[currency]) || FALLBACK_RATES[currency] || 1;
  return amount / rate;
};

/**
 * Scans raw CSV text for currency markers and returns the detected currency code.
 * Checks column headers and a few data rows (first 2000 chars).
 * Defaults to 'EUR' if nothing is found.
 */
export const COUNTRY_TO_MARKETPLACE = {
  FR: 'Amazon.fr',
  BE: 'Amazon.com.be',
  DE: 'Amazon.de',
  ES: 'Amazon.es',
  IT: 'Amazon.it',
  NL: 'Amazon.nl',
  PL: 'Amazon.pl',
  UK: 'Amazon.co.uk',
  SE: 'Amazon.se',
  IE: 'Amazon.ie',
};

export const VAT_RATES = {
  'Amazon.fr':     0.20,
  'Amazon.com.be': 0.21,
  'Amazon.de':     0.19,
  'Amazon.es':     0.21,
  'Amazon.it':     0.22,
  'Amazon.nl':     0.21,
  'Amazon.pl':     0.23,
  'Amazon.se':     0.25,
  'Amazon.co.uk':  0.20,
  'Amazon.ie':     0.23,
};

export const getVatDivisor = (marketplace) => {
  return 1 + (VAT_RATES[marketplace] || 0.20);
};

export const detectCurrencyFromCSV = (csvContent) => {
  const sample = csvContent.slice(0, 2000);

  // Order matters: more specific patterns first
  if (/PLN/i.test(sample)) return 'PLN';
  if (/£/.test(sample) || /GBP/i.test(sample)) return 'GBP';
  if (/SEK/i.test(sample) || /\bkr\b/.test(sample)) return 'SEK';
  return 'EUR';
};
